import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

const AUDIO_SRC = '/assets/audio/gemstone_vibe.mp3';

const CrystalMusicPlayer = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [pulse, setPulse] = useState(0);

  const formatTime = (t) => {
    if (!t || isNaN(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        // Mobile: ensure audio is loaded before playing
        if (audio.readyState < 2) {
          audio.load();
        }
        const p = audio.play();
        if (p !== undefined) {
          await p;
        }
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Playback failed:', err);
    }
  }, [isPlaying]);

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // Support touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duration;
  };

  // Pulse animation loop while playing
  useEffect(() => {
    let raf;
    const tick = () => {
      setPulse(Date.now());
      raf = requestAnimationFrame(tick);
    };
    if (isPlaying) {
      raf = requestAnimationFrame(tick);
    }
    return () => raf && cancelAnimationFrame(raf);
  }, [isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    };
    const onMeta = () => setDuration(audio.duration);
    const onEnd = () => { setIsPlaying(false); setProgress(0); setCurrentTime(0); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
    };
  }, []);

  // Derive animation intensity
  const t = pulse / 1000;
  const breathe = isPlaying ? 0.5 + Math.sin(t * 1.2) * 0.3 + Math.sin(t * 2.7) * 0.2 : 0.15;
  const drift = isPlaying ? Math.sin(t * 0.8) * 15 : 0;

  return (
    <div className="flex flex-col items-center" data-testid="crystal-music-player">

      {/* Crystal + Smoke Container */}
      <div className="relative mb-6 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96">

        {/* ===== SMOKE / MIST LAYERS ===== */}
        {isPlaying && (
          <>
            <div className="absolute inset-0 pointer-events-none will-change-transform" style={{ opacity: 0.3 + breathe * 0.3 }}>
              <div className="smoke-layer-1 absolute w-[200%] h-[200%] -top-1/2 -left-1/2"
                style={{ transform: `translate3d(${drift}px, ${-drift * 0.6}px, 0)` }} />
            </div>
            <div className="absolute inset-0 pointer-events-none will-change-transform" style={{ opacity: 0.2 + breathe * 0.25 }}>
              <div className="smoke-layer-2 absolute w-[200%] h-[200%] -top-1/2 -left-1/2"
                style={{ transform: `translate3d(${-drift * 0.8}px, ${drift * 0.4}px, 0)` }} />
            </div>
            <div className="absolute inset-0 pointer-events-none will-change-transform" style={{ opacity: 0.15 + breathe * 0.2 }}>
              <div className="smoke-layer-3 absolute w-[150%] h-[200%] -top-1/2 left-[-25%]" />
            </div>
          </>
        )}

        {/* ===== OUTER GLOW RINGS ===== */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-full transition-all duration-700"
            style={{
              boxShadow: isPlaying
                ? `0 0 ${40 + breathe * 60}px ${10 + breathe * 20}px rgba(157,78,221,${breathe * 0.5}),
                   0 0 ${60 + breathe * 80}px ${20 + breathe * 30}px rgba(201,169,110,${breathe * 0.3}),
                   0 0 ${100 + breathe * 120}px ${40 + breathe * 50}px rgba(255,107,157,${breathe * 0.15})`
                : `0 0 30px 5px rgba(157,78,221,0.15), 0 0 60px 15px rgba(201,169,110,0.08)`,
            }}
          />
        </div>

        {/* ===== ORBITING PARTICLES ===== */}
        {isPlaying && [...Array(8)].map((_, i) => {
          const angle = (t * (0.3 + i * 0.08)) + (i * Math.PI * 2 / 8);
          const radius = 100 + Math.sin(t * 0.5 + i) * 15;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const size = 2 + Math.sin(t * 2 + i) * 1.5;
          const colors = ['#c9a96e', '#9D4EDD', '#FF6B9D', '#FFB800', '#7FDBFF', '#00C853', '#c9a96e', '#9D4EDD'];
          return (
            <div
              key={i}
              className="absolute rounded-full pointer-events-none will-change-transform"
              style={{
                width: size, height: size, background: colors[i],
                left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)`,
                boxShadow: `0 0 ${6 + breathe * 10}px ${colors[i]}`,
                opacity: 0.5 + breathe * 0.5,
              }}
            />
          );
        })}

        {/* ===== CRYSTAL SHAPE — tap target ===== */}
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={togglePlay}
            onTouchEnd={(e) => { e.preventDefault(); togglePlay(); }}
            className="relative w-44 h-44 sm:w-56 sm:h-56 md:w-64 md:h-64 cursor-pointer group focus:outline-none active:scale-95 transition-transform"
            style={{
              clipPath: 'polygon(50% 2%, 82% 14%, 98% 50%, 82% 86%, 50% 98%, 18% 86%, 2% 50%, 18% 14%)',
              transform: `scale(${1 + breathe * 0.04})`,
              WebkitTapHighlightColor: 'transparent',
            }}
            aria-label={isPlaying ? 'Pause music' : 'Play music'}
            data-testid="crystal-player-shape"
          >
            {/* Crystal body */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a2e] via-[#2a1040] to-[#0a0a20]" />

            {/* Internal glow */}
            <div className="absolute inset-0" style={{
              background: `radial-gradient(circle at 45% 40%, rgba(157,78,221,${isPlaying ? 0.25 + breathe * 0.3 : 0.1}) 0%, transparent 60%),
                           radial-gradient(circle at 60% 65%, rgba(201,169,110,${isPlaying ? 0.15 + breathe * 0.2 : 0.05}) 0%, transparent 50%)`,
            }} />

            {/* Facet highlights */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-[8%] left-[12%] w-[45%] h-[0.5px] bg-gradient-to-r from-white/40 to-transparent rotate-[25deg]" />
              <div className="absolute top-[22%] right-[8%] w-[38%] h-[0.5px] bg-gradient-to-l from-white/30 to-transparent rotate-[-18deg]" />
              <div className="absolute bottom-[18%] left-[18%] w-[32%] h-[0.5px] bg-gradient-to-r from-[#c9a96e]/40 to-transparent rotate-[12deg]" />
            </div>

            {/* Light beams when playing */}
            {isPlaying && (
              <div className="absolute inset-0 overflow-hidden">
                <div className="crystal-beam-1 absolute w-1 bg-gradient-to-b from-transparent via-[#c9a96e]/30 to-transparent" style={{ height: '120%', top: '-10%', left: '30%' }} />
                <div className="crystal-beam-2 absolute w-1 bg-gradient-to-b from-transparent via-[#9D4EDD]/25 to-transparent" style={{ height: '120%', top: '-10%', left: '60%' }} />
                <div className="crystal-beam-3 absolute h-1 bg-gradient-to-r from-transparent via-[#FF6B9D]/20 to-transparent" style={{ width: '120%', left: '-10%', top: '45%' }} />
              </div>
            )}

            {/* Play/Pause icon */}
            <div className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${isPlaying ? 'opacity-0 group-hover:opacity-100 group-active:opacity-100' : 'opacity-100'}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                {isPlaying ? (
                  <Pause className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                ) : (
                  <Play className="w-6 h-6 sm:w-7 sm:h-7 text-white ml-1" />
                )}
              </div>
            </div>
          </button>
        </div>

        {/* ===== RISING EMBERS ===== */}
        {isPlaying && [...Array(6)].map((_, i) => (
          <div
            key={`ember-${i}`}
            className="absolute pointer-events-none ember-particle"
            style={{
              left: `${30 + i * 8}%`, bottom: '15%',
              animationDelay: `${i * 0.7}s`,
              animationDuration: `${3 + i * 0.5}s`,
            }}
          >
            <div className="w-1 h-1 rounded-full" style={{
              background: i % 2 === 0 ? '#c9a96e' : '#9D4EDD',
              boxShadow: `0 0 4px ${i % 2 === 0 ? '#c9a96e' : '#9D4EDD'}`,
            }} />
          </div>
        ))}
      </div>

      {/* Song Title */}
      <div className="text-center mb-4">
        <h3 className="font-serif text-lg sm:text-xl md:text-2xl text-white" data-testid="song-title">
          Cocoa Kholey's <span className="text-[#c9a96e]">Gemstone Vibe</span>
        </h3>
        <p className="text-xs sm:text-sm text-white/40">Gemstone Minerals Official</p>
      </div>

      {/* Progress Bar — touch-friendly */}
      <div className="w-full max-w-sm px-4">
        <div
          className="relative h-2 sm:h-1.5 bg-white/10 rounded-full cursor-pointer overflow-hidden group"
          onClick={handleSeek}
          onTouchStart={handleSeek}
          data-testid="progress-bar"
        >
          <div
            className="absolute top-0 left-0 h-full rounded-full"
            style={{
              width: `${progress}%`,
              background: isPlaying
                ? 'linear-gradient(90deg, #9D4EDD, #c9a96e, #FF6B9D)'
                : 'linear-gradient(90deg, #9D4EDD, #c9a96e)',
              transition: 'width 0.1s linear',
            }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 sm:w-3.5 sm:h-3.5 rounded-full bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progress}% - 8px)`, boxShadow: '0 0 8px rgba(157,78,221,0.5)' }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-white/30">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls — large touch targets */}
      <div className="flex items-center gap-4 mt-4">
        <button
          onClick={togglePlay}
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 active:scale-90 ${
            isPlaying
              ? 'bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] shadow-xl shadow-[#9D4EDD]/40'
              : 'bg-gradient-to-br from-[#c9a96e] to-[#FFB800] shadow-lg shadow-[#c9a96e]/30'
          }`}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          data-testid="play-pause-btn"
        >
          {isPlaying ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-0.5" />}
        </button>
        <button
          onClick={toggleMute}
          className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          data-testid="mute-btn"
        >
          {isMuted ? <VolumeX className="w-4 h-4 text-white/40" /> : <Volume2 className="w-4 h-4 text-[#c9a96e]" />}
        </button>
      </div>

      {/* Audio element — playsInline for mobile, preload for faster start */}
      <audio ref={audioRef} src={AUDIO_SRC} preload="metadata" playsInline />

      {/* CSS animations — GPU-accelerated for mobile */}
      <style>{`
        .smoke-layer-1 {
          background: radial-gradient(ellipse at 30% 50%, rgba(157,78,221,0.15) 0%, transparent 50%),
                      radial-gradient(ellipse at 70% 30%, rgba(201,169,110,0.1) 0%, transparent 45%),
                      radial-gradient(ellipse at 50% 70%, rgba(255,107,157,0.08) 0%, transparent 55%);
          animation: smoke-drift-1 12s ease-in-out infinite;
          will-change: transform;
        }
        .smoke-layer-2 {
          background: radial-gradient(ellipse at 60% 40%, rgba(201,169,110,0.12) 0%, transparent 50%),
                      radial-gradient(ellipse at 25% 60%, rgba(157,78,221,0.1) 0%, transparent 45%),
                      radial-gradient(ellipse at 75% 75%, rgba(127,219,255,0.06) 0%, transparent 50%);
          animation: smoke-drift-2 15s ease-in-out infinite;
          will-change: transform;
        }
        .smoke-layer-3 {
          background: radial-gradient(ellipse at 40% 80%, rgba(157,78,221,0.1) 0%, transparent 40%),
                      radial-gradient(ellipse at 60% 20%, rgba(201,169,110,0.08) 0%, transparent 35%);
          animation: smoke-rise 10s ease-in-out infinite;
          will-change: transform;
        }
        @keyframes smoke-drift-1 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          25% { transform: translate3d(20px, -10px, 0) scale(1.05); }
          50% { transform: translate3d(-15px, -20px, 0) scale(1.1); }
          75% { transform: translate3d(10px, -5px, 0) scale(1.03); }
        }
        @keyframes smoke-drift-2 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          33% { transform: translate3d(-25px, -15px, 0) scale(1.08); }
          66% { transform: translate3d(15px, -25px, 0) scale(1.04); }
        }
        @keyframes smoke-rise {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate3d(0, -30px, 0) scale(1.15); opacity: 1; }
        }
        .crystal-beam-1 {
          animation: beam-sweep 6s ease-in-out infinite;
          will-change: transform;
        }
        .crystal-beam-2 {
          animation: beam-sweep 8s ease-in-out infinite reverse;
          will-change: transform;
        }
        .crystal-beam-3 {
          animation: beam-sweep-h 7s ease-in-out infinite;
          will-change: transform;
        }
        @keyframes beam-sweep {
          0%, 100% { transform: rotate(-15deg) translate3d(-20px, 0, 0); opacity: 0.3; }
          50% { transform: rotate(15deg) translate3d(20px, 0, 0); opacity: 0.7; }
        }
        @keyframes beam-sweep-h {
          0%, 100% { transform: rotate(-10deg) translate3d(0, -15px, 0); opacity: 0.2; }
          50% { transform: rotate(10deg) translate3d(0, 15px, 0); opacity: 0.5; }
        }
        .ember-particle {
          animation: ember-rise 3s ease-out infinite;
          will-change: transform, opacity;
        }
        @keyframes ember-rise {
          0% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.8; }
          50% { transform: translate3d(10px, -60px, 0) scale(0.8); opacity: 0.5; }
          100% { transform: translate3d(-5px, -120px, 0) scale(0.3); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default CrystalMusicPlayer;
